import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Briefcase, FileText, Star, Phone, HelpCircle } from 'lucide-react';

/**
 * SiteLinks component for improved SEO
 * Displays important site links prominently to help Google understand site structure
 * These links encourage Google to display sitelinks in search results
 */
export function SiteLinks() {
  const mainLinks = [
    {
      title: 'Services',
      description: 'View all our removal and moving services',
      to: '/services',
      icon: Briefcase
    },
    {
      title: 'Areas We Cover',
      description: 'See our coverage across the Midlands',
      to: '/areas-we-cover',
      icon: MapPin
    },
    {
      title: 'Reviews',
      description: '4.8★ Rating from trusted customers',
      to: '/reviews',
      icon: Star
    },
    {
      title: 'Get Quote',
      description: 'Free moving quote in minutes',
      to: '/quote',
      icon: Phone
    },
    {
      title: 'FAQ',
      description: 'Find answers to common questions',
      to: '/faq',
      icon: HelpCircle
    },
    {
      title: 'Blog',
      description: 'Moving tips and helpful guides',
      to: '/blog',
      icon: FileText
    }
  ];

  return (
    <section
      className="py-12 px-4 bg-gradient-to-r from-slate-50 to-blue-50"
    >
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Explore VIP House Movers</h2>
        <p className="text-gray-600 mb-8">Quick access to our main services and information</p>
        
        <nav
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          aria-label="Main site links"
        >
          {mainLinks.map((link, index) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.to}
                to={link.to}
                className="group p-5 bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
                title={link.description}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors flex items-center gap-2">
                      <Icon className="w-5 h-5" />
                      {link.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-2">{link.description}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0 mt-1" />
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Hidden schema for search engines */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: mainLinks.map((link, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: link.title,
                item: `https://www.viphousemovers.com${link.to}`
              }))
            })
          }}
        />
      </div>
    </section>
  );
}

/**
 * Service Category Links - for Services page
 */
export function ServiceCategoryLinks() {
  const serviceCategories = [
    { name: 'House Removals', path: '/services/house-removals' },
    { name: 'Office Removals', path: '/services/office-removals' },
    { name: 'Man and Van', path: '/services/man-and-van' },
    { name: 'Packing Services', path: '/services/packing-services' },
    { name: 'Storage Solutions', path: '/services/storage-solutions' },
    { name: 'Furniture Assembly', path: '/services/furniture-assembly' },
    { name: 'Long Distance Removals', path: '/services/long-distance-removals' },
    { name: 'Same Day Moves', path: '/services/same-day-moves' }
  ];

  return (
    <nav className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {serviceCategories.map(service => (
        <Link
          key={service.path}
          to={service.path}
          className="px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-900 rounded-lg font-medium transition-colors text-center"
        >
          {service.name}
        </Link>
      ))}
    </nav>
  );
}

/**
 * Location Links - for Areas We Cover page
 */
export function LocationLinks() {
  const regions = [
    {
      name: 'North West England',
      keyTowns: [
        { name: 'Manchester', path: '/areas-we-cover/manchester' },
        { name: 'Liverpool', path: '/areas-we-cover/liverpool' },
        { name: 'Chester', path: '/areas-we-cover/chester' },
        { name: 'Lancaster', path: '/areas-we-cover/lancaster' },
        { name: 'Warrington', path: '/areas-we-cover/warrington' },
      ],
    },
    {
      name: 'North East England',
      keyTowns: [
        { name: 'Newcastle', path: '/areas-we-cover/newcastle-upon-tyne' },
        { name: 'Sunderland', path: '/areas-we-cover/sunderland' },
        { name: 'Durham', path: '/areas-we-cover/durham' },
        { name: 'Darlington', path: '/areas-we-cover/darlington' },
      ],
    },
    {
      name: 'Yorkshire & Humber',
      keyTowns: [
        { name: 'Leeds', path: '/areas-we-cover/leeds' },
        { name: 'Sheffield', path: '/areas-we-cover/sheffield' },
        { name: 'York', path: '/areas-we-cover/york' },
        { name: 'Bradford', path: '/areas-we-cover/bradford' },
        { name: 'Doncaster', path: '/areas-we-cover/doncaster' },
      ],
    },
    {
      name: 'East Midlands',
      keyTowns: [
        { name: 'Nottingham', path: '/areas-we-cover/nottingham' },
        { name: 'Leicester', path: '/areas-we-cover/leicester' },
        { name: 'Derby', path: '/areas-we-cover/derby' },
        { name: 'Northampton', path: '/areas-we-cover/northampton' },
        { name: 'Lincoln', path: '/areas-we-cover/lincoln' },
      ],
    },
    {
      name: 'West Midlands',
      keyTowns: [
        { name: 'Birmingham', path: '/areas-we-cover/birmingham' },
        { name: 'Wolverhampton', path: '/areas-we-cover/wolverhampton' },
        { name: 'Coventry', path: '/areas-we-cover/coventry' },
        { name: 'Stoke-on-Trent', path: '/areas-we-cover/stoke-on-trent' },
        { name: 'Stafford', path: '/areas-we-cover/stafford' },
      ],
    },
    {
      name: 'East of England',
      keyTowns: [
        { name: 'Cambridge', path: '/areas-we-cover/cambridge' },
        { name: 'Norwich', path: '/areas-we-cover/norwich' },
        { name: 'Ipswich', path: '/areas-we-cover/ipswich' },
        { name: 'Luton', path: '/areas-we-cover/luton' },
        { name: 'Watford', path: '/areas-we-cover/watford' },
      ],
    },
    {
      name: 'South East',
      keyTowns: [
        { name: 'Brighton', path: '/areas-we-cover/brighton' },
        { name: 'Reading', path: '/areas-we-cover/reading' },
        { name: 'Oxford', path: '/areas-we-cover/oxford' },
        { name: 'Portsmouth', path: '/areas-we-cover/portsmouth' },
        { name: 'Southampton', path: '/areas-we-cover/southampton' },
      ],
    },
    {
      name: 'South West',
      keyTowns: [
        { name: 'Bristol', path: '/areas-we-cover/bristol' },
        { name: 'Plymouth', path: '/areas-we-cover/plymouth' },
        { name: 'Exeter', path: '/areas-we-cover/exeter' },
        { name: 'Bournemouth', path: '/areas-we-cover/bournemouth' },
        { name: 'Swindon', path: '/areas-we-cover/swindon' },
      ],
    },
    {
      name: 'Greater London',
      keyTowns: [
        { name: 'Croydon', path: '/areas-we-cover/croydon' },
        { name: 'Bromley', path: '/areas-we-cover/bromley' },
        { name: 'Enfield', path: '/areas-we-cover/enfield' },
        { name: 'Harrow', path: '/areas-we-cover/harrow' },
        { name: 'Wimbledon', path: '/areas-we-cover/wimbledon' },
      ],
    },
  ];

  return (
    <div className="space-y-8">
      {regions.map((region) => (
        <div key={region.name}>
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">{region.name}</h3>
          <nav className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {region.keyTowns.map((location) => (
              <Link
                key={location.path}
                to={location.path}
                className="px-4 py-3 bg-green-50 hover:bg-green-100 text-green-900 rounded-lg font-medium transition-colors text-center"
              >
                {location.name}
              </Link>
            ))}
          </nav>
        </div>
      ))}
    </div>
  );
}
